<template>
  <v-dialog
    :model-value="modelValue"
    max-width="920"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card class="manage-plans">
      <v-card-title class="manage-plans__title">
        {{ t('marketing-agent.delete-list-title') }}
      </v-card-title>
      <v-card-text>
        <div class="manage-plans__filters">
          <span class="manage-plans__filter-label">{{ t('marketing-agent.filter-label') }}</span>
          <v-btn
            v-for="option in filters"
            :key="option"
            class="manage-plans__filter"
            size="small"
            rounded="pill"
            color="primary"
            :variant="filter === option ? 'flat' : 'outlined'"
            :append-icon="filter === option && option !== 'all' ? 'mdi-close' : undefined"
            @click="onFilter(option)"
          >
            {{ t(`marketing-agent.filter-${option}`) }}
          </v-btn>
        </div>

        <v-data-table
          class="manage-plans__table"
          :headers="headers"
          :items="visibleRows"
          item-value="id"
          :loading="loading"
          :loading-text="t('marketing-agent.delete-list-loading')"
          :items-per-page="-1"
          hide-default-footer
          fixed-header
          height="360"
        >
          <template #[`item.topic`]="{ item }">
            <span class="manage-plans__topic">{{ item.topic || '—' }}</span>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn
              icon="mdi-delete-outline"
              size="small"
              variant="text"
              color="error"
              :disabled="!item.deletable || deletingId === item.id"
              :loading="deletingId === item.id"
              :aria-label="t('marketing-agent.delete-confirm')"
              @click="emit('delete', item.id)"
            />
          </template>
          <template #no-data>
            <RetryNotice
              v-if="error"
              :message="t('marketing-agent.delete-list-error')"
              :state="listRetry.state.value"
              :loading="retrying"
              @retry="onRetry"
            />
            <span
              v-else
              class="manage-plans__empty"
              >{{ t('marketing-agent.delete-list-empty') }}</span
            >
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions class="manage-plans__actions">
        <v-spacer />
        <v-btn
          variant="text"
          color="primary"
          @click="emit('update:modelValue', false)"
        >
          {{ t('marketing-agent.close') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          rounded="pill"
          @click="emit('create')"
        >
          {{ t('marketing-agent.approval.create-new') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { useQuery } from '@vue/apollo-composable'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { MarketingPlanType } from '@/generated/graphql'

import { useRetryLimit } from './composables/useRetryLimit'
import { reportError } from './lib/reportError'
import GET_MARKETING_PLANS from './queries/getMarketingPlans.gql'
import RetryNotice from './RetryNotice.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  deletingId: { type: String, default: null },
})

const emit = defineEmits(['update:modelValue', 'delete', 'create'])

const { t, locale } = useI18n()
const filter = ref('all')
const filters = ['approved', 'planned', 'all']

const { result, loading, error, refetch } = useQuery(GET_MARKETING_PLANS, null, () => ({
  enabled: props.modelValue,
  fetchPolicy: 'network-only',
}))

const listRetry = useRetryLimit('load-plans-list', { error })
const retrying = ref(false)

// A failed load is not "no plans": the table shows a retry instead.
watch(error, (current, previous) => {
  if (current && !previous) reportError('load-plans-list', current)
})

// The query's error ref carries the failure; don't leave the promise unhandled.
function reloadList() {
  refetch()?.catch(() => {})
}

async function onRetry() {
  retrying.value = true
  try {
    await listRetry.run(() => refetch(), { operation: 'reload-plans-list' })
  } catch {
    // RetryNotice shows the next attempt, or the support hint.
  } finally {
    retrying.value = false
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    filter.value = 'all'
    reloadList()
  }
)

watch(
  () => props.deletingId,
  (id, previous) => {
    if (previous && !id) reloadList()
  }
)

const headers = computed(() => [
  { title: t('marketing-agent.column-dates'), key: 'range', sortable: false },
  { title: t('marketing-agent.column-topic'), key: 'topic', sortable: false },
  { title: t('marketing-agent.column-posts'), key: 'postCount', sortable: false, align: 'end' },
  {
    title: t('marketing-agent.column-actions'),
    key: 'actions',
    sortable: false,
    align: 'end',
    width: 120,
  },
])

const rows = computed(() =>
  (result.value?.plans ?? []).map((plan) => ({
    id: plan.id,
    range: dateRange(plan),
    topic: plan.topicName || '',
    postCount: plan.postCount ?? 0,
    planType: plan.planType,
    deletable: plan.planType !== MarketingPlanType.Executing,
  }))
)

const visibleRows = computed(() => {
  if (filter.value === 'all') return rows.value
  if (filter.value === 'planned') {
    return rows.value.filter(
      (row) =>
        row.planType === MarketingPlanType.Planned || row.planType === MarketingPlanType.Executing
    )
  }
  return rows.value.filter((row) => row.planType === MarketingPlanType.Approved)
})

function onFilter(option) {
  filter.value = filter.value === option && option !== 'all' ? 'all' : option
}

function dateRange(plan) {
  const dates = (plan.slots || [])
    .map((slot) => slot.scheduledLocal?.date)
    .filter(Boolean)
    .sort()
  const start = dates[0] || dateOnly(plan.startDate)
  const end = dates[dates.length - 1] || start
  if (!start) return ''
  if (!end || end === start) return formatDay(start)
  return `${formatDay(start)} - ${formatDay(end)}`
}

function dateOnly(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function formatDay(iso) {
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return iso
  return new Intl.DateTimeFormat(locale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(year, month - 1, day))
}
</script>

<style scoped>
.manage-plans__title {
  background: rgb(var(--v-theme-primary));
  color: #fff;
  font-size: 22px;
  font-weight: 700;
}

.manage-plans__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.manage-plans__filter-label {
  margin-right: 4px;
  color: rgb(var(--v-theme-primary));
  font-size: 15px;
  font-weight: 700;
}

.manage-plans__filter {
  text-transform: none;
  letter-spacing: 0;
}

.manage-plans__table :deep(thead th) {
  background: #eef4f4 !important;
  color: rgb(var(--v-theme-primary)) !important;
  font-size: 14px;
  font-weight: 700;
}

.manage-plans__table :deep(td) {
  font-size: 14px;
}

.manage-plans__topic {
  color: rgb(var(--v-theme-orange));
  font-weight: 600;
}

.manage-plans__empty {
  color: #1a1a1a;
}

.manage-plans__actions {
  padding: 8px 16px 16px;
}

.manage-plans__actions :deep(.v-btn) {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
}
</style>
